import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatZAR } from "@/lib/api";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal } = useCart();

  return (
    <>
      {drawerOpen && (
        <div
          data-testid="cart-overlay"
          className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-ivory shadow-2xl transition-transform duration-500 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-line">
          <p className="font-serif text-xl">Your bag <span className="text-stone text-sm">({items.length})</span></p>
          <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} className="p-2">
            <X strokeWidth={1.5} className="w-5 h-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="small-label">Your bag is empty</p>
            <p className="font-serif text-2xl mt-3">Begin your edit.</p>
            <Link
              to="/shop"
              onClick={() => setDrawerOpen(false)}
              data-testid="cart-empty-shop-link"
              className="btn-primary mt-6"
            >
              Discover Wigs
            </Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-5 overflow-y-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
              {items.map((item) => (
                <div key={item.key} className="flex gap-4">
                  <Link to={`/products/${item.slug}`} onClick={() => setDrawerOpen(false)} className="shrink-0">
                    <img src={item.image} alt={item.title} className="w-20 h-24 object-cover rounded-xl bg-nude" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight">{item.title}</p>
                    <p className="small-label mt-1">{item.length}″ · {item.density} · {item.color}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border border-stone-line rounded-full">
                        <button data-testid={`qty-minus-${item.key}`} onClick={() => updateQty(item.key, item.quantity - 1)} className="p-1.5"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="px-3 text-xs">{item.quantity}</span>
                        <button data-testid={`qty-plus-${item.key}`} onClick={() => updateQty(item.key, item.quantity + 1)} className="p-1.5"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <p className="text-sm font-medium">{formatZAR(item.price * item.quantity)}</p>
                    </div>
                  </div>
                  <button data-testid={`remove-${item.key}`} onClick={() => removeItem(item.key)} className="p-1 text-stone hover:text-charcoal">
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-stone-line px-6 py-5 space-y-4">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="font-medium" data-testid="cart-subtotal">{formatZAR(subtotal)}</span>
              </div>
              <p className="text-xs text-stone">Shipping and taxes calculated at checkout.</p>
              <Link
                to="/checkout"
                onClick={() => setDrawerOpen(false)}
                data-testid="cart-checkout-button"
                className="btn-primary w-full"
              >
                Checkout
              </Link>
              <Link
                to="/cart"
                onClick={() => setDrawerOpen(false)}
                data-testid="cart-view-bag-link"
                className="block text-center text-xs uppercase tracking-luxe text-stone hover:text-charcoal"
              >
                View full bag
              </Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
